import { Component, OnInit, OnDestroy, HostListener, ChangeDetectionStrategy } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, FormControl, AbstractControl, ValidationErrors } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { UserService } from '../../../core/services/user.service';
import { GroupService } from '../../../core/services/group.service';
import { UpdateUserRequest } from '../../../core/models/user.model';
import { RolePermissionService } from '../../../core/services/role-permission.service';
import { Subject, of, takeUntil } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Component({
  selector: 'app-user-edit',
  standalone: true,
  imports: [ReactiveFormsModule],
  templateUrl: './user-edit.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./user-edit.component.scss'],
})
export class EditUserComponent implements OnInit, OnDestroy {
  userForm: FormGroup;
  isLoading = false;
  isLoadingUser = false;
  showSnackbar = false;
  snackbarMessage = '';
  userId = '';
  userData: any = null;

  iesList: { sigle: string; label: string }[] = [];
  etablissementsList: { sigle: string; label: string }[] = [];
  filteredIesList: { sigle: string; label: string }[] = [];
  filteredEtabList: { sigle: string; label: string }[] = [];
  isLoadingIes = false;
  isLoadingEtab = false;
  isIesDropdownOpen = false;
  isEtabDropdownOpen = false;
  iesSearchControl = new FormControl('');
  etabSearchControl = new FormControl('');
  private destroy$ = new Subject<void>();

  isCurrentUserIesAdmin = false;
  currentUserIesSigle: string | null = null;

  basicInfoExpanded = true;
  assignmentExpanded = true;
  configExpanded = true;

  // ─── Modal de succès ─────────────────────────────────
  showSuccessModal = false;
  savedUser: any = null;

  private originalIesCode: string | null = null;
  private originalEtabCodes: string[] = [];

  static atLeastOneSelected(control: AbstractControl): ValidationErrors | null {
    return Array.isArray(control.value) && control.value.length > 0 ? null : { required: true };
  }

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private userService: UserService,
    private groupService: GroupService,
    private rolePermissionService: RolePermissionService
  ) {
    this.userForm = this.createForm();
  }

  ngOnInit(): void {
    this.isLoadingUser = true; // évite un flash sans skeleton
    this.loadAdminContext();

    this.userForm.get('emailVerified')?.valueChanges.subscribe((v: boolean) => this.updateEmailControlState(v));

    this.userForm.get('sigleIes')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((sigle) => {
        if (sigle) {
          this.loadEtablissements(sigle);
          const etabCtrl = this.userForm.get('sigleEtablissements');
          if (etabCtrl?.disabled) etabCtrl.enable();
        } else {
          this.userForm.get('sigleEtablissements')?.setValue([]);
          this.userForm.get('sigleEtablissements')?.disable();
          this.etablissementsList = [];
          this.filteredEtabList = [];
          this.isLoadingEtab = false;
        }
      });

    this.iesSearchControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(t => this.filteredIesList = this.filterIesList(t || ''));

    this.etabSearchControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(t => this.filteredEtabList = this.filterEtabList(t || ''));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  createForm(): FormGroup {
    return this.fb.group({
      username: ['', [Validators.required, Validators.minLength(3)]],
      email: ['', [Validators.required, Validators.email]],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      isActive: [true],
      emailVerified: [false],
      sigleIes: [{ value: null, disabled: false }],
      sigleEtablissements: [[]]
    });
  }

  // ─── Contexte admin ─────────────────────────────────────────────
  private loadAdminContext(): void {
    this.isCurrentUserIesAdmin = this.rolePermissionService.isIesAdmin();
    if (!this.isCurrentUserIesAdmin) {
      this.getUserData();
      return;
    }
    const adminIesCode = this.rolePermissionService.getCurrentUserIesCode();
    if (!adminIesCode) {
      this.getUserData();
      return;
    }
    this.userService.getSigleByCode(adminIesCode)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (sigle) => { this.currentUserIesSigle = sigle; this.getUserData(); },
        error: () => { this.currentUserIesSigle = adminIesCode; this.getUserData(); }
      });
  }

  // ─── Chargement user ────────────────────────────────────────────
  getUserData(): void {
    this.isLoading = true;
    this.isLoadingUser = true;
    // Flags de chargement affichés dès maintenant (avant HTTP) :
    if (!this.isCurrentUserIesAdmin) {
      this.isLoadingIes = true;
    }
    this.isLoadingEtab = true;

    this.userId = this.route.snapshot.paramMap.get('id') || '';
    if (!this.userId) {
      this.isLoading = false;
      this.isLoadingUser = false;
      this.isLoadingIes = false;
      this.isLoadingEtab = false;
      return;
    }

    this.userService.getUserById(this.userId).subscribe({
      next: (user) => {
        this.userData = user;
        this.populateForm(user);
        this.isLoading = false;
        this.isLoadingUser = false;
      },
      error: (error) => {
        console.error('Erreur chargement user:', error);
        this.showSnackbarMessage('Erreur lors du chargement des données utilisateur', 'error');
        this.isLoading = false;
        this.isLoadingUser = false;
        this.isLoadingIes = false;
        this.isLoadingEtab = false;
      }
    });
  }

  // ─── Remplissage du formulaire ──────────────────────────────────
  populateForm(user: any): void {
    this.userForm.patchValue({
      username: user.username || '',
      email: user.email || '',
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      isActive: user.enabled ?? true,
      emailVerified: user.emailVerified || false,
    });

    this.userForm.get('username')?.disable();
    this.updateEmailControlState(user.emailVerified);

    const iesCode = user.sigleIes as string | null;
    const etabCodes: string[] = user.sigleEtablissements || [];

    this.userService.getSigleCodeMap()
      .pipe(takeUntil(this.destroy$), catchError(err => {
        console.error('Map code→sigle indisponible:', err);
        this.showSnackbarMessage(
          'Impossible de charger la correspondance IES/établissements. ' +
          'La modification de l\'IES est désactivée pour éviter des erreurs.',
          'error');
        return of({} as Record<string, string>);
      }))
      .subscribe((map: Record<string, string>) => {
        const mapOk = map && Object.keys(map).length > 0;

        // On garde le CODE comme valeur de référence, séparément du SIGLE affiché
        this.originalIesCode = iesCode;
        this.originalEtabCodes = [...etabCodes];

        let iesSigle: string | null = null;
        let etabsSigles: string[] = [];

        if (mapOk) {
          iesSigle = iesCode ? (map[iesCode] ?? null) : null;
          etabsSigles = etabCodes.map(c => map[c]).filter((s): s is string => !!s);
        }

        // Si la map a échoué, on laisse vides et on désactive les champs IES/étabs
        this.userForm.patchValue({
          sigleIes: iesSigle,
          sigleEtablissements: etabsSigles
        }, { emitEvent: false });

        if (!mapOk) {
          this.userForm.get('sigleIes')?.disable({ emitEvent: false });
          this.userForm.get('sigleEtablissements')?.disable({ emitEvent: false });
        }

        this.applyIesLogic(user, iesSigle);
      });
  }

  private applyIesLogic(user: any, sigleIes: string | null): void {
    const sigleIesCtrl = this.userForm.get('sigleIes');
    const sigleEtablissementsCtrl = this.userForm.get('sigleEtablissements');
    const hasIes = !!sigleIes;

    if (this.isCurrentUserIesAdmin && this.currentUserIesSigle) {
      sigleIesCtrl?.disable();
      sigleIesCtrl?.setValue(this.currentUserIesSigle, { emitEvent: false });
      this.loadEtablissements(this.currentUserIesSigle);
      sigleEtablissementsCtrl?.enable();
    } else {
      this.loadIesList();
      if (hasIes) {
        sigleIesCtrl?.setValue(sigleIes, { emitEvent: false });
        this.loadEtablissements(sigleIes!);
        sigleEtablissementsCtrl?.enable();
      } else {
        sigleIesCtrl?.enable();
        sigleEtablissementsCtrl?.disable();
        sigleEtablissementsCtrl?.setValue([]);
        this.isLoadingEtab = false; // <-- important : pas de chargement à faire
      }
    }
    this.updateValidators();
  }

  private updateValidators(): void {
    const iesCtrl = this.userForm.get('sigleIes');
    const etabCtrl = this.userForm.get('sigleEtablissements');
    if (!!iesCtrl?.value) iesCtrl.setValidators([Validators.required]);
    else iesCtrl?.clearValidators();
    etabCtrl?.clearValidators();
    iesCtrl?.updateValueAndValidity({ emitEvent: false });
    etabCtrl?.updateValueAndValidity({ emitEvent: false });
  }

  // ─── Listes ─────────────────────────────────────────────────────
  private loadIesList(): void {
    if (this.isCurrentUserIesAdmin) { this.iesList = []; this.filteredIesList = []; return; }
    if (this.iesList.length > 0) { this.isLoadingIes = false; return; }
    this.isLoadingIes = true;
    this.groupService.getRootGroups()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (labels: string[]) => {
          this.iesList = labels.map(l => ({ sigle: this.extractSigle(l), label: l }));
          this.filteredIesList = [...this.iesList];
          this.isLoadingIes = false;
        },
        error: () => {
          this.showSnackbarMessage('Impossible de charger la liste des IES', 'error');
          this.isLoadingIes = false;
        }
      });
  }

  private loadEtablissements(sigleIes: string): void {
    if (!sigleIes) {
      this.etablissementsList = [];
      this.filteredEtabList = [];
      this.userForm.get('sigleEtablissements')?.setValue([], { emitEvent: false });
      this.isLoadingEtab = false;
      return;
    }
    this.isLoadingEtab = true;
    this.groupService.getSubgroupsByParentSigle(sigleIes)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (labels: string[]) => {
          this.etablissementsList = labels.map(l => ({ sigle: this.extractSigle(l), label: l }));
          this.filteredEtabList = [...this.etablissementsList];

          // Pré-sélection : on garde uniquement les sigles qui existent dans la liste chargée
          const current = this.userForm.get('sigleEtablissements')?.value || [];
          const valid = current.filter((s: string) => this.etablissementsList.some(e => e.sigle === s));
          if (valid.length !== current.length) {
            this.userForm.get('sigleEtablissements')?.setValue(valid, { emitEvent: false });
          }
          this.isLoadingEtab = false;
          this.updateValidators();
        },
        error: () => {
          this.showSnackbarMessage('Impossible de charger les établissements', 'error');
          this.isLoadingEtab = false;
        }
      });
  }

  private extractSigle(label: string): string {
    const match = label.match(/\(([^)]+)\)\s*$/);
    return match ? match[1].trim() : label.trim();
  }

  private filterIesList(term: string) {
    if (!term) return this.iesList;
    const t = term.toLowerCase();
    return this.iesList.filter(i => i.label.toLowerCase().includes(t) || i.sigle.toLowerCase().includes(t));
  }
  private filterEtabList(term: string) {
    if (!term) return this.etablissementsList;
    const t = term.toLowerCase();
    return this.etablissementsList.filter(e => e.label.toLowerCase().includes(t) || e.sigle.toLowerCase().includes(t));
  }

  // ─── Interactions IES ───────────────────────────────────────────
  toggleIesDropdown(): void {
    if (!this.userForm.get('sigleIes')?.disabled) this.isIesDropdownOpen = !this.isIesDropdownOpen;
  }
  selectIes(sigle: string): void {
    const ctrl = this.userForm.get('sigleIes');
    if (ctrl?.value !== sigle) {
      ctrl?.setValue(sigle);
      ctrl?.markAsDirty();
      this.userForm.markAsDirty();
    }
    this.isIesDropdownOpen = false;
  }
  /** Nouveau : permet de retirer l'IES du user (SUPER_ADMIN uniquement) */
  clearIes(): void {
    const ctrl = this.userForm.get('sigleIes');
    if (ctrl?.disabled) return;
    ctrl?.setValue(null);
    ctrl?.markAsDirty();
    this.userForm.markAsDirty();
    this.isIesDropdownOpen = false;
  }

  // ─── Interactions étabs ─────────────────────────────────────────
  toggleEtabDropdown(): void {
    if (!this.userForm.get('sigleEtablissements')?.disabled) this.isEtabDropdownOpen = !this.isEtabDropdownOpen;
  }
  isEtabSelected(sigle: string): boolean {
    return (this.userForm.get('sigleEtablissements')?.value || []).includes(sigle);
  }
  toggleEtabSelection(sigle: string, event: any): void {
    const ctrl = this.userForm.get('sigleEtablissements');
    let current = ctrl?.value || [];
    current = event.target.checked ? [...current, sigle] : current.filter((s: string) => s !== sigle);
    ctrl?.setValue(current);
    ctrl?.markAsDirty();
    ctrl?.markAsTouched();
    this.userForm.markAsDirty();
    this.updateValidators();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('#ies-multiselect')) this.isIesDropdownOpen = false;
    if (!target.closest('#etab-multiselect')) this.isEtabDropdownOpen = false;
  }

  // ─── Email ──────────────────────────────────────────────────────
  updateEmailControlState(isVerified: boolean): void {
    const email = this.userForm.get('email');
    if (!email) return;
    if (isVerified) email.disable();
    else {
      email.enable();
      email.setValidators([Validators.required, Validators.email]);
      email.updateValueAndValidity();
    }
  }

  // ─── Submit ─────────────────────────────────────────────────────
  onSubmit(): void {
    if (this.userForm.invalid) { this.markAllFieldsAsTouched(); return; }
    this.isLoading = true;

    const formData = this.userForm.getRawValue();
    const updateData: UpdateUserRequest = {
      // username: formData.username,
      email: formData.email,
      firstName: formData.firstName,
      lastName: formData.lastName,
      enabled: formData.isActive,
      emailVerified: formData.emailVerified,
      sigleIes: formData.sigleIes || null,
      sigleEtablissements: formData.sigleEtablissements || [],
      // attributes: { phone: [''], department: [''] }
    };

    this.userService.updateUser(this.userId, updateData).subscribe({
      next: (response: any) => {
        this.userService.invalidateCache();
        if (response && response.user) {
          this.userService.updateUserInLocalStorage(response.user);
          this.savedUser = response.user;
        }
        this.isLoading = false;
        this.userForm.markAsPristine();
        this.showSuccessModal = true; // <-- pop-up au lieu de redirection
      },
      error: (error) => {
        console.error('Erreur modification:', error);
        this.showSnackbarMessage('Erreur lors de la modification', 'error');
        this.isLoading = false;
      }
    });
  }

  onCancel(): void { this.router.navigate(['/users']); }

  // ─── Modal succès ──────────────────────────────────────────────
  onModalClose(): void { this.showSuccessModal = false; }

  onModalGoToList(): void {
    this.showSuccessModal = false;
    this.router.navigate(['/users']);
  }

  // ─── Utilitaires ────────────────────────────────────────────────
  isFieldInvalid(name: string): boolean {
    const f = this.userForm.get(name);
    return !!(f && f.invalid && f.touched);
  }
  hasError(name: string, type: string): boolean {
    return !!this.userForm.get(name)?.hasError(type);
  }
  markAllFieldsAsTouched(): void {
    Object.keys(this.userForm.controls).forEach(k => this.userForm.get(k)?.markAsTouched());
  }
  showSnackbarMessage(message: string, type: 'success' | 'error' | 'warning' = 'success'): void {
    this.snackbarMessage = message;
    this.showSnackbar = true;
    setTimeout(() => this.hideSnackbar(), 4000);
  }
  hideSnackbar(): void { this.showSnackbar = false; }
}